import { DependencyList, ReactNode } from "react";
import { useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import { ReactHookFormCheckbox } from "../Inputs/Checkbox";
import { DeleteModalFieldset } from "./__styles__/DeleteModal";
import { Emphasis } from "./__styles__/Typography";
import { CommonModal, CommonModalComponentProps } from "./CommonModal";
import Modal from "./Modal";
import { Body } from "./Typography";

export function getDefaultDeleteBody(toBeDeletedObject: string) {
  return (
    <Body size="large" type="regular">
      Are you sure you would like to delete this {toBeDeletedObject}?{" "}
      <Emphasis>This action cannot be undone.</Emphasis>
    </Body>
  );
}

export type DeleteModalFormProps = {
  headerText: string;
  subheaderText?: string;
  Content?: ReactNode;
  deleteButtonText?: string;
  onClose: () => void;
  onDelete?: () => void | Promise<unknown>;
  disabled?: boolean;
  confirmations?: Array<string>;
  confirmationPreamble?: string;
  loading?: boolean;
} & Pick<CommonModalComponentProps, "size">;

export const useDeleteModal = ({
  dependencies,
  ...rest
}: Omit<DeleteModalFormProps, "onClose"> & {
  dependencies?: DependencyList;
}) => {
  const [showDeleteModal, hideDeleteModal] = useModal(
    () => (
      <Modal onRequestClose={hideDeleteModal}>
        <DeleteModalForm {...rest} onClose={hideDeleteModal} />
      </Modal>
    ),
    dependencies ? [...dependencies] : undefined,
  );

  return [showDeleteModal, hideDeleteModal] as const;
};

export const DeleteModalForm = ({
  headerText,
  subheaderText,
  Content,
  deleteButtonText = "Yes, delete",
  size,
  onClose,
  onDelete,
  disabled = false,
  confirmations,
  confirmationPreamble = "By deleting this, you confirm that:",
  loading = false,
}: DeleteModalFormProps) => {
  const {
    control,
    formState: { isValid },
  } = useForm({ mode: "onChange" });

  const handleSubmit = onDelete
    ? async () => {
        await onDelete();
        onClose();
      }
    : undefined;

  const defaultConfirmationContent = confirmations ? (
    <Body size="default" type="regular">
      This action <strong>cannot</strong> be undone.
    </Body>
  ) : undefined;

  return (
    <CommonModal
      title={headerText}
      subtitle={subheaderText}
      onSubmit={handleSubmit}
      onCancel={onClose}
      loading={loading}
      disabled={disabled || !isValid}
      size={size}
      variant="destructive"
      primaryButtonText={deleteButtonText}
      content={
        <>
          {confirmations?.length && (
            <>
              <Body size="default" type="regular">
                {confirmationPreamble}
              </Body>
              <DeleteModalFieldset>
                {confirmations.map((label, index) => (
                  <ReactHookFormCheckbox
                    key={index}
                    control={control}
                    name={`confirmation_${index}`}
                    label={label}
                    required
                  />
                ))}
              </DeleteModalFieldset>
            </>
          )}
          {Content ?? defaultConfirmationContent}
        </>
      }
    />
  );
};
